import { Quote, Star } from "lucide-react";
import { useTranslation } from "react-i18next";

const CONTENT = {
  es: {
    eyebrow: "Lo que dicen quienes ya lo usan",
    title: "Una sola herramienta para todo el proyecto",
    subtitle:
      "Marketing, desarrollo, producción, eventos, educación… Sprintroll se adapta al tamaño y estilo de cada equipo, sin sacrificar profundidad ni potencia.",
    items: [
      {
        name: "Ana G.", role: "Directora de marketing", company: "Agencia Nube Roja", stars: 5,
        quote: "Pasamos de tener cuatro herramientas distintas a solo Sprintroll. Con las vistas Rolls, Sprints y Matriz cubrimos desde la planeación anual hasta el kanban del día. Mi equipo dejó de perderse entre pestañas.",
        avatar: "AG", accent: "from-rose-500 to-pink-500",
      },
      {
        name: "Carlos M.", role: "Tech lead", company: "Estudio de desarrollo Kaizen", stars: 5,
        quote: "Es la herramienta más completa que hemos probado. Sprints con dependencias, matriz de prioridades, comentarios en Markdown, checklists y adjuntos… todo bajo el mismo tablero. Reemplazó a Jira y Trello en un solo movimiento.",
        avatar: "CM", accent: "from-blue-500 to-sky-500",
      },
      {
        name: "Sofía R.", role: "Productora de eventos", company: "Estudio Aurora", stars: 5,
        quote: "Coordino equipos creativos, proveedores y clientes al mismo tiempo. Sprintroll me permite ver la producción en Gantt, priorizar tareas críticas en la matriz y compartir tableros solo con quienes deben verlos. Es profesional sin ser complicado.",
        avatar: "SR", accent: "from-emerald-500 to-teal-500",
      },
    ],
  },
  en: {
    eyebrow: "What people already using it say",
    title: "One tool for the whole project",
    subtitle:
      "Marketing, development, production, events, education… Sprintroll fits every team's size and style without sacrificing depth or power.",
    items: [
      {
        name: "Ana G.", role: "Marketing Director", company: "Nube Roja Agency", stars: 5,
        quote: "We went from four different tools to just Sprintroll. With Rolls, Sprints and Matrix views we cover everything from yearly planning to today's kanban. My team no longer gets lost between tabs.",
        avatar: "AG", accent: "from-rose-500 to-pink-500",
      },
      {
        name: "Carlos M.", role: "Tech Lead", company: "Kaizen Dev Studio", stars: 5,
        quote: "The most complete tool we've tried. Sprints with dependencies, priority matrix, Markdown comments, checklists and attachments… all in one board. It replaced Jira and Trello in a single move.",
        avatar: "CM", accent: "from-blue-500 to-sky-500",
      },
      {
        name: "Sofía R.", role: "Events Producer", company: "Aurora Studio", stars: 5,
        quote: "I coordinate creative teams, vendors and clients at the same time. Sprintroll lets me see the production as a Gantt, prioritize critical tasks on the matrix, and share boards only with the right people. Professional without being complicated.",
        avatar: "SR", accent: "from-emerald-500 to-teal-500",
      },
    ],
  },
};

export default function TestimonialsSection() {
  const { i18n } = useTranslation();
  const c = CONTENT[i18n.language?.startsWith("en") ? "en" : "es"];
  return (
    <section
      id="testimonials"
      className="relative py-20 md:py-28"
      data-testid="landing-testimonials"
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto">
          <span className="inline-block text-[11px] uppercase tracking-[0.2em] font-semibold text-blue-600 dark:text-blue-400">
            {c.eyebrow}
          </span>
          <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight mt-2">
            {c.title}
          </h2>
          <p className="text-muted-foreground mt-4">{c.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {c.items.map((t, i) => (
            <article
              key={i}
              data-testid={`testimonial-${i}`}
              className="glass-card rounded-2xl p-6 flex flex-col relative overflow-hidden hover:-translate-y-0.5 hover:shadow-xl transition"
            >
              <Quote className="absolute -top-2 -right-2 h-24 w-24 text-blue-500/10 rotate-180" />
              <div className="flex items-center gap-1 mb-4 relative">
                {Array.from({ length: t.stars }).map((_, j) => (
                  <Star key={j} className="h-4 w-4 text-amber-400 fill-amber-400" />
                ))}
              </div>
              <p className="text-sm md:text-base leading-relaxed text-foreground/90 flex-1 relative">
                “{t.quote}”
              </p>
              <div className="flex items-center gap-3 mt-6 pt-5 border-t border-white/30 dark:border-white/10 relative">
                <div className={`h-11 w-11 rounded-full bg-gradient-to-br ${t.accent} text-white text-sm font-semibold grid place-items-center shadow-md`}>
                  {t.avatar}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{t.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{t.role} · {t.company}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
